import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useRealtimeOrders } from "@/lib/useRealtimeOrders";
import { playChime } from "@/lib/chime";
import { GBP, statusColor, pretty, ORDER_STATUSES, PAYMENT_STATUSES, PAYMENT_METHODS } from "@/lib/helpers";
import { PageHeader } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Trash2, ShoppingBag, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function NewOrderDialog({ rid, menu, onCreated }) {
  const [open, setOpen] = useState(false);
  const [orderType, setOrderType] = useState("TAKEAWAY");
  const [source, setSource] = useState("STAFF_DASHBOARD");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [collectionTime, setCollectionTime] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState("UNPAID");
  const [paymentMethod, setPaymentMethod] = useState("UNKNOWN");
  const [fulfillment, setFulfillment] = useState("COLLECTION");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");

  const addItem = (item) => {
    setLines((prev) => {
      const ex = prev.find((l) => l.menu_item_id === item.id);
      if (ex) return prev.map((l) => (l.menu_item_id === item.id ? { ...l, quantity: l.quantity + 1 } : l));
      return [...prev, { menu_item_id: item.id, name: item.name, unit_price: item.price, quantity: 1 }];
    });
  };
  const total = lines.reduce((s, l) => s + l.unit_price * l.quantity, 0);

  const submit = async () => {
    if (lines.length === 0) return toast.error("Add at least one item");
    if (orderType === "TAKEAWAY" && fulfillment === "DELIVERY" && (!address.trim() || !postcode.trim()))
      return toast.error("Delivery orders need an address and postcode");
    try {
      await api.post(`/restaurants/${rid}/orders`, {
        order_type: orderType, source, customer_name: name || "Guest", customer_phone: phone || null,
        collection_time: collectionTime || null, special_notes: notes || null,
        payment_status: paymentStatus, payment_method: paymentMethod,
        fulfillment_type: orderType === "TAKEAWAY" ? fulfillment : "COLLECTION",
        delivery_address: orderType === "TAKEAWAY" && fulfillment === "DELIVERY" ? (address || null) : null,
        delivery_postcode: orderType === "TAKEAWAY" && fulfillment === "DELIVERY" ? (postcode || null) : null,
        items: lines.map((l) => ({ menu_item_id: l.menu_item_id, quantity: l.quantity })),
      });
      toast.success("Order created");
      setOpen(false); setLines([]); setName(""); setPhone(""); setNotes(""); setCollectionTime("");
      setFulfillment("COLLECTION"); setAddress(""); setPostcode("");
      onCreated();
    } catch (e) {
      toast.error("Could not create order");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-11 gap-2" data-testid="new-order-button"><Plus className="h-4 w-4" /> New order</Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Create manual order</DialogTitle></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Order type</Label>
            <Select value={orderType} onValueChange={setOrderType}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="order-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TAKEAWAY">Takeaway</SelectItem>
                <SelectItem value="DINE_IN">Dine-in</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Source</Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="h-11 mt-1.5" data-testid="order-source-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="STAFF_DASHBOARD">Staff dashboard</SelectItem>
                <SelectItem value="AI_PHONE">After AI handover (phone)</SelectItem>
                <SelectItem value="WALK_IN">Walk-in</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Customer name</Label>
            <Input className="h-11 mt-1.5" value={name} onChange={(e) => setName(e.target.value)} data-testid="order-customer-name" />
          </div>
          <div>
            <Label>Phone</Label>
            <Input className="h-11 mt-1.5" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="order-customer-phone" />
          </div>
          <div>
            <Label>Collection time</Label>
            <Input className="h-11 mt-1.5" placeholder="e.g. 19:30" value={collectionTime} onChange={(e) => setCollectionTime(e.target.value)} />
          </div>
          {orderType === "TAKEAWAY" && (
            <div>
              <Label>Fulfillment</Label>
              <Select value={fulfillment} onValueChange={setFulfillment}>
                <SelectTrigger className="h-11 mt-1.5" data-testid="fulfillment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="COLLECTION">Collection</SelectItem>
                  <SelectItem value="DELIVERY">Delivery</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {orderType === "TAKEAWAY" && fulfillment === "DELIVERY" && (
            <>
              <div className="sm:col-span-2">
                <Label>Delivery address</Label>
                <Input className="h-11 mt-1.5" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="order-delivery-address" />
              </div>
              <div>
                <Label>Postcode</Label>
                <Input className="h-11 mt-1.5" value={postcode} onChange={(e) => setPostcode(e.target.value)} data-testid="order-delivery-postcode" />
              </div>
            </>
          )}
        </div>

        <div className="mt-2">
          <Label>Add items</Label>
          <div className="grid sm:grid-cols-2 gap-2 mt-1.5 max-h-40 overflow-y-auto">
            {menu.map((m) => (
              <button key={m.id} type="button" onClick={() => addItem(m)}
                data-testid={`add-item-${m.id}`}
                className="flex items-center justify-between rounded-lg border border-border p-2.5 text-left hover:bg-secondary transition-colors">
                <span className="text-sm">{m.name}
                </span>
                <span className="text-sm font-medium">{GBP(m.price)}</span>
              </button>
            ))}
          </div>
        </div>

        {lines.length > 0 && (
          <div className="rounded-xl border border-border divide-y">
            {lines.map((l) => (
              <div key={l.menu_item_id} className="flex items-center justify-between p-3">
                <span className="text-sm">{l.name}</span>
                <div className="flex items-center gap-3">
                  <Input type="number" min={1} value={l.quantity} className="h-9 w-16"
                    onChange={(e) => setLines((p) => p.map((x) => x.menu_item_id === l.menu_item_id ? { ...x, quantity: Math.max(1, +e.target.value) } : x))} />
                  <span className="text-sm font-medium w-16 text-right">{GBP(l.unit_price * l.quantity)}</span>
                  <Button size="icon" variant="ghost" onClick={() => setLines((p) => p.filter((x) => x.menu_item_id !== l.menu_item_id))}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label>Payment status</Label>
            <Select value={paymentStatus} onValueChange={setPaymentStatus}>
              <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label>Payment method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger className="h-11 mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{PAYMENT_METHODS.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <Textarea placeholder="Special notes" value={notes} onChange={(e) => setNotes(e.target.value)} />

        <DialogFooter className="items-center justify-between sm:justify-between">
          <span className="font-display text-xl font-bold">Total {GBP(total)}</span>
          <Button onClick={submit} className="h-11" data-testid="submit-order">Create order</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetail({ order, tables = [], onClose, onUpdated }) {
  const [status, setStatus] = useState(order.status);
  const [pStatus, setPStatus] = useState(order.payment_status);
  const [pMethod, setPMethod] = useState(order.payment_method);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get(`/orders/${order.id}/payment-events`).then((r) => setEvents(r.data)).catch(() => {});
  }, [order.id]);

  const saveStatus = async () => {
    await api.patch(`/orders/${order.id}/status`, { status });
    toast.success("Status updated"); onUpdated();
  };
  const savePayment = async () => {
    await api.patch(`/orders/${order.id}/payment`, { payment_status: pStatus, payment_method: pMethod });
    toast.success("Payment updated"); onUpdated();
    api.get(`/orders/${order.id}/payment-events`).then((r) => setEvents(r.data));
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            {order.order_number}
            <Badge className={statusColor(order.status)} variant="outline">{pretty(order.status)}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="text-sm text-muted-foreground">
          {order.customer_name} · {order.customer_phone || "no phone"} · {pretty(order.order_type)} · {pretty(order.source)}
        </div>
        {order.order_type === "DINE_IN" && (() => {
          const ids = order.table_ids?.length ? order.table_ids : (order.table_id ? [order.table_id] : []);
          const names = ids.map((id) => tables.find((t) => t.id === id)?.table_name || "?");
          return names.length > 0 ? <div className="text-sm"><Badge variant="outline">Table(s): {names.join(", ")}</Badge></div> : null;
        })()}
        {order.order_type === "TAKEAWAY" && order.fulfillment_type && (
          <div className="text-sm">
            <Badge variant="outline" className="mr-2">{pretty(order.fulfillment_type)}</Badge>
            {order.fulfillment_type === "DELIVERY" && order.delivery_address && (
              <span className="text-muted-foreground">{order.delivery_address}{order.delivery_postcode ? `, ${order.delivery_postcode}` : ""}</span>
            )}
          </div>
        )}

        {order.allergy_handover_required && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5" /> Allergy review required before completing this order.
          </div>
        )}

        <div className="rounded-xl border border-border divide-y">
          {order.items.map((it) => (
            <div key={it.id} className="p-3">
              <div className="flex justify-between">
                <span className="font-medium">{it.quantity}× {it.item_name_snapshot}</span>
                <span>{GBP(it.total_price)}</span>
              </div>
              {it.allergens_snapshot?.length > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  Allergens: {it.allergens_snapshot.map((a) => `${a.name} (${a.relation})`).join(", ")}
                </p>
              )}
            </div>
          ))}
        </div>
        {order.allergen_disclosure_text && (
          <p className="text-sm text-muted-foreground">{order.allergen_disclosure_text}</p>
        )}
        {order.delivery_fee > 0 && (
          <div className="flex justify-between text-sm text-muted-foreground"><span>Delivery fee</span><span>{GBP(order.delivery_fee)}</span></div>
        )}
        <div className="flex justify-between font-display text-lg font-bold"><span>Total</span><span>{GBP(order.total)}</span></div>

        <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-border">
          <div>
            <Label>Order status</Label>
            <div className="flex gap-2 mt-1.5">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="h-11" data-testid="order-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ORDER_STATUSES.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={saveStatus} className="h-11" data-testid="save-status">Save</Button>
            </div>
          </div>
          <div>
            <Label>Payment</Label>
            <div className="flex flex-col gap-2 mt-1.5">
              <Select value={pStatus} onValueChange={setPStatus}>
                <SelectTrigger className="h-11" data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={pMethod} onValueChange={setPMethod}>
                <SelectTrigger className="h-11" data-testid="payment-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{PAYMENT_METHODS.map((s) => <SelectItem key={s} value={s}>{pretty(s)}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={savePayment} className="h-11" data-testid="save-payment">Update payment</Button>
            </div>
          </div>
        </div>

        {events.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Payment history</p>
            <div className="space-y-1">
              {events.map((e) => (
                <p key={e.id} className="text-sm text-muted-foreground">
                  {pretty(e.previous_status)} → {pretty(e.new_status)} · {pretty(e.payment_method)} · {e.changed_by}
                </p>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Orders() {
  const { rid } = useParams();
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [tables, setTables] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    api.get(`/restaurants/${rid}/orders`).then((r) => setOrders(r.data)).catch(() => {});
  }, [rid]);

  useEffect(() => {
    load();
    api.get(`/restaurants/${rid}/menu/items`).then((r) => setMenu(r.data.filter((m) => m.is_available))).catch(() => {});
    api.get(`/restaurants/${rid}/tables`).then((r) => setTables(r.data)).catch(() => {});
  }, [rid, load]);

  useRealtimeOrders(rid, (data) => {
    if (data?.type === "order_created") {
      playChime();
      toast.success("New order received");
    }
    load();
  });

  const tableNames = (o) => {
    const ids = (o.table_ids && o.table_ids.length ? o.table_ids : (o.table_id ? [o.table_id] : []));
    return ids.map((id) => tables.find((t) => t.id === id)?.table_name || "?");
  };

  const q = search.trim().toLowerCase();
  const filtered = orders.filter((o) =>
    (filter === "ALL" ? true : filter === "TAKEAWAY" ? o.order_type === "TAKEAWAY" :
    filter === "DINE_IN" ? o.order_type === "DINE_IN" : o.status === filter)
    && (!q || (o.customer_name || "").toLowerCase().includes(q) || (o.customer_phone || "").toLowerCase().includes(q) || (o.order_number || "").toLowerCase().includes(q)));

  return (
    <div>
      <PageHeader title="Orders" subtitle="Central order dashboard — takeaway, dine-in and AI phone orders.">
        <NewOrderDialog rid={rid} menu={menu} onCreated={load} />
      </PageHeader>

      <div className="mb-4 max-w-md">
        <Input data-testid="orders-search" placeholder="Search by customer name, phone or order #"
          value={search} onChange={(e) => setSearch(e.target.value)} className="h-11" />
      </div>

      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList className="flex-wrap h-auto">
          {["ALL", "TAKEAWAY", "DINE_IN", "NEW", "PREPARING", "READY", "COMPLETED"].map((f) => (
            <TabsTrigger key={f} value={f} data-testid={`filter-${f}`}>{pretty(f)}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((o) => (
          <Card key={o.id} data-testid={`order-card-${o.id}`}
            className="p-5 rounded-2xl cursor-pointer hover:border-primary/40 transition-colors"
            onClick={() => setSelected(o)}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-display font-bold">{o.order_number}</p>
                <p className="text-sm text-muted-foreground">{o.customer_name} · {pretty(o.order_type)}
                  {o.order_type === "DINE_IN" && tableNames(o).length > 0 && <span> · {tableNames(o).join(", ")}</span>}
                  {o.order_type === "TAKEAWAY" && o.fulfillment_type && <span> · {pretty(o.fulfillment_type)}</span>}
                </p>
              </div>
              <Badge className={statusColor(o.status)} variant="outline">{pretty(o.status)}</Badge>
            </div>
            <div className="mt-3 space-y-0.5">
              {o.items.slice(0, 3).map((it) => (
                <p key={it.id} className="text-sm">{it.quantity}× {it.item_name_snapshot}</p>
              ))}
              {o.items.length > 3 && <p className="text-xs text-muted-foreground">+{o.items.length - 3} more</p>}
            </div>
            {o.allergy_handover_required && (
              <p className="text-xs text-red-600 mt-2 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Allergy review</p>
            )}
            <div className="mt-3 flex items-center justify-between pt-3 border-t border-border">
              <Badge className={statusColor(o.payment_status)} variant="outline">{pretty(o.payment_status)}</Badge>
              <div className="text-right">
                {o.delivery_fee > 0 && <p className="text-[11px] text-muted-foreground">inc. {GBP(o.delivery_fee)} delivery</p>}
                <span className="font-display font-bold">{GBP(o.total)}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-16 text-muted-foreground">
          <ShoppingBag className="h-10 w-10 mx-auto mb-3 opacity-40" /> No orders in this view.
        </div>
      )}

      {selected && <OrderDetail order={selected} tables={tables} onClose={() => setSelected(null)}
        onUpdated={() => { load(); setSelected(null); }} />}
    </div>
  );
}
